import { useState, useRef, forwardRef } from 'react';
import { Input as HeadlessInput } from '@headlessui/react';
import { FieldLabel } from './FieldLabel';

export type InputType = 'text' | 'password' | 'email' | 'date' | 'time' | 'number' | 'url' | 'tel' | 'search';
export type InputSize = 'small' | 'medium' | 'large';

export type InputProps = {
  type?: InputType;
  value?: string;
  onChange?: (value: string) => void;
  /** Raw change event - useful for libraries that need the DOM element (e.g. react-international-phone). */
  onRawChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  label?: string;
  /** Help text shown as a `?` tooltip beside the label. */
  helpText?: string;
  error?: string;
  disabled?: boolean;
  size?: InputSize;
  className?: string;
  id?: string;
  name?: string;
  autoComplete?: string;
  required?: boolean;
  min?: string;
  max?: string;
  step?: string | number;
  autoFocus?: boolean;
  readOnly?: boolean;
  minLength?: number;
  maxLength?: number;
  /** Remove left border-radius (for composing with a left-side selector). */
  noLeftRounding?: boolean;
  /** Regex pattern for inline validation on blur. */
  regex?: string;
  /** Error message shown when regex validation fails. */
  regexError?: string;
  'aria-label'?: string;
};

// Flexible URL validation - accepts "acme.com", "www.acme.com", "https://acme.com"
function isValidUrl(url: string): boolean {
  if (!url) return true;
  const pattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z]{2,})+(\/\S*)?$/;
  return pattern.test(url.trim());
}

const sizeClasses = {
  small: 'h-8 px-3 text-xs',
  medium: 'h-10 px-4 text-sm',
  large: 'h-12 px-5 text-base',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      value,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
      placeholder,
      label,
      helpText,
      error,
      disabled = false,
      size = 'medium',
      className = '',
      id,
      name,
      autoComplete,
      required = false,
      min,
      max,
      step,
      autoFocus,
      readOnly,
      minLength,
      maxLength,
      noLeftRounding,
      regex,
      regexError = 'Invalid format',
      onRawChange,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
    const [urlError, setUrlError] = useState<string | null>(null);
    const [regexErrMsg, setRegexErrMsg] = useState<string | null>(null);
    const [minLengthErr, setMinLengthErr] = useState<string | null>(null);
    const inputRef = useRef<HTMLDivElement>(null);

    const inputId = id || `input-${name || Math.random().toString(36).substr(2, 9)}`;
    // Use 'text' for URL type to avoid browser's strict validation
    const inputType = type === 'password' && showPassword ? 'text' : type === 'url' ? 'text' : type;

    // Combined error: external error prop takes precedence, then internal URL validation, then regex, then minLength
    const displayError = error || urlError || regexErrMsg || minLengthErr;

    const baseClasses = `input-base ${sizeClasses[size]} motion-reduce:transition-none`;
    const focusClasses = isFocused && !displayError && !disabled ? 'input-focus' : '';
    const errorClasses = displayError ? 'input-error' : '';

    const handleBlur = () => {
      setIsFocused(false);
      // Validate URL on blur
      if (type === 'url' && value && !isValidUrl(value)) {
        setUrlError('Please enter a valid URL (e.g., acme.com)');
      } else {
        setUrlError(null);
      }
      // Validate regex on blur
      if (regex && value) {
        try {
          const re = new RegExp(regex);
          if (!re.test(value as string)) {
            setRegexErrMsg(regexError);
          } else {
            setRegexErrMsg(null);
          }
        } catch {
          setRegexErrMsg(null);
        }
      } else {
        setRegexErrMsg(null);
      }
      // Validate minLength on blur
      if (minLength !== undefined && minLength !== null && value && (value as string).length < minLength) {
        setMinLengthErr(`Must be at least ${minLength} characters`);
      } else {
        setMinLengthErr(null);
      }
      onBlur?.();
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!inputRef.current || disabled) return;
      const rect = inputRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    };

    return (
      <div className={`relative ${className}`}>
        {label && (
          <FieldLabel htmlFor={inputId} required={required} helpText={helpText}>
            {label}
          </FieldLabel>
        )}

        <div
          ref={inputRef}
          onMouseMove={handleMouseMove}
          className="relative group"
        >
          <HeadlessInput
            ref={ref}
            id={inputId}
            name={name}
            type={inputType}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onRawChange?.(e);
              onChange?.(e.target.value);
            }}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete={autoComplete}
            required={required}
            min={min}
            max={max}
            step={step}
            autoFocus={autoFocus}
            readOnly={readOnly}
            maxLength={maxLength}
            aria-label={ariaLabel || label}
            aria-invalid={!!displayError}
            aria-describedby={displayError ? `${inputId}-error` : undefined}
            onFocus={() => {
              setIsFocused(true);
              onFocus?.();
            }}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            className={`
              ${baseClasses}
              ${focusClasses}
              ${errorClasses}
              ${type === 'password' ? 'pr-12' : ''}
              ${noLeftRounding ? '!rounded-l-none' : ''}
            `}
          />

          {/* Mouse-following glow effect on focus */}
          {isFocused && !disabled && !displayError && (
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-lg"
              style={{
                background: `radial-gradient(circle 100px at ${mousePosition.x}% ${mousePosition.y}%, rgb(34 211 238 / 0.08), transparent 80%)`,
              }}
            />
          )}

          {/* Password visibility toggle */}
          {type === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={disabled}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/90 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-cyan/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded disabled:opacity-50"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          )}
        </div>

        {displayError && (
          <p
            id={`${inputId}-error`}
            className="mt-2 text-sm text-danger-red flex items-center gap-1"
            role="alert"
          >
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            {displayError}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
